import { useState, useRef, useEffect, useCallback } from 'react';
import {
  Container, Title, Text, Stack, MultiSelect, TextInput, Button,
  Paper, ScrollArea, Badge, Group, Accordion, Loader, Card, Divider, Tooltip,
} from '@/components/ui';
import { useMantineColorScheme, ActionIcon } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconArrowLeft, IconSend, IconRobot, IconUser, IconMessageChatbot, IconCopy, IconCheck } from '@tabler/icons-react';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useAiDocumentService } from '@actschurch/ccms-sdk/react';
import { useEnrichedAuth } from '../../../../hooks/useEnrichedAuth';
import type { DocumentSource, DocumentQueryResponse } from '@actschurch/ccms-sdk';

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  sources?: DocumentSource[];
  confidence?: number;
  durationMs?: number;
}

export default function DocumentQAPage() {
  const navigate = useNavigate();
  const documentService = useAiDocumentService();
  const { user } = useEnrichedAuth();
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === 'dark';

  const [selectedDocIds, setSelectedDocIds] = useState<string[]>([]);
  const [question, setQuestion] = useState('');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isAsking, setIsAsking] = useState(false);
  const [copiedIdx, setCopiedIdx] = useState<number | null>(null);
  const viewport = useRef<HTMLDivElement>(null);

  // Configure marked to handle line breaks and GFM
  marked.setOptions({ breaks: true, gfm: true });

  const renderMarkdown = (text: string): string => {
    const rawHtml = marked.parse(text, { async: false }) as string;
    return DOMPurify.sanitize(rawHtml);
  };

  const handleCopy = async (text: string, idx: number) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedIdx(idx);
      notifications.show({ title: 'Copied', message: 'Answer copied to clipboard', color: 'green' });
      setTimeout(() => setCopiedIdx(null), 2000);
    } catch {
      notifications.show({ title: 'Copy failed', message: 'Could not copy to clipboard', color: 'red' });
    }
  };

  const { data: documents } = useQuery({
    queryKey: ['ai', 'documents'],
    queryFn: () => documentService.listDocuments(0),
    enabled: !!user,
  });

  const docOptions = (documents ?? []).map((d) => ({
    value: String(d.documentID),
    label: d.fileName,
  }));

  const scrollToBottom = useCallback(() => {
    setTimeout(() => {
      viewport.current?.scrollTo({ top: viewport.current.scrollHeight, behavior: 'smooth' });
    }, 50);
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  const handleAsk = async () => {
    const trimmed = question.trim();
    if (!trimmed || isAsking) return;

    const userMsg: ChatMessage = { role: 'user', content: trimmed };
    setMessages((prev) => [...prev, userMsg]);
    setQuestion('');
    setIsAsking(true);

    try {
      const docIds = selectedDocIds.length > 0 ? selectedDocIds.map(Number) : undefined;
      const response: DocumentQueryResponse = await documentService.askQuestion({
        vendorID: 0,
        question: trimmed,
        documentIds: docIds,
      });

      const assistantMsg: ChatMessage = {
        role: 'assistant',
        content: response.answer,
        sources: response.sources,
        confidence: response.confidence,
        durationMs: response.durationMs,
      };
      setMessages((prev) => [...prev, assistantMsg]);
    } catch (err) {
      const errorMsg: ChatMessage = {
        role: 'assistant',
        content: err instanceof Error ? err.message : 'An error occurred while processing your question.',
      };
      setMessages((prev) => [...prev, errorMsg]);
    } finally {
      setIsAsking(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleAsk();
    }
  };

  return (
    <Container fluid py="xl">
      {/* Scoped markdown styles */}
      <style>{`
        .ai-markdown-content p { margin: 0 0 0.75em 0; }
        .ai-markdown-content p:last-child { margin-bottom: 0; }
        .ai-markdown-content h1, .ai-markdown-content h2, .ai-markdown-content h3,
        .ai-markdown-content h4, .ai-markdown-content h5, .ai-markdown-content h6 {
          margin: 1em 0 0.5em 0; font-weight: 600; line-height: 1.3;
        }
        .ai-markdown-content h1 { font-size: 1.4em; }
        .ai-markdown-content h2 { font-size: 1.25em; }
        .ai-markdown-content h3 { font-size: 1.1em; }
        .ai-markdown-content ul, .ai-markdown-content ol { margin: 0 0 0.75em 0; padding-left: 1.5em; }
        .ai-markdown-content li { margin-bottom: 0.25em; }
        .ai-markdown-content code {
          background: ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'};
          padding: 2px 6px; border-radius: 4px; font-size: 0.9em;
          font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
        }
        .ai-markdown-content pre {
          background: ${isDark ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.06)'};
          padding: 0.75em; border-radius: 6px; overflow-x: auto; margin: 0.75em 0;
        }
        .ai-markdown-content pre code { background: none; padding: 0; }
        .ai-markdown-content blockquote {
          border-left: 3px solid ${isDark ? 'var(--mantine-color-blue-5)' : 'var(--mantine-color-blue-6)'};
          padding-left: 0.75em; margin: 0.75em 0; color: ${isDark ? 'var(--mantine-color-gray-4)' : 'var(--mantine-color-gray-7)'};
        }
        .ai-markdown-content a {
          color: ${isDark ? 'var(--mantine-color-blue-4)' : 'var(--mantine-color-blue-6)'};
          text-decoration: underline;
        }
        .ai-markdown-content table { border-collapse: collapse; margin: 0.75em 0; }
        .ai-markdown-content th, .ai-markdown-content td {
          border: 1px solid ${isDark ? 'var(--mantine-color-dark-4)' : 'var(--mantine-color-gray-3)'};
          padding: 6px 10px; text-align: left;
        }
        .ai-markdown-content th { background: ${isDark ? 'var(--mantine-color-dark-6)' : 'var(--mantine-color-gray-1)'}; font-weight: 600; }
        .ai-markdown-content hr { border: 0; border-top: 1px solid ${isDark ? 'var(--mantine-color-dark-4)' : 'var(--mantine-color-gray-3)'}; margin: 1em 0; }
        .ai-markdown-content strong { font-weight: 600; }
      `}</style>
      <Stack gap="lg" h="calc(100vh - 120px)">
        {/* Header */}
        <Group justify="space-between" wrap="wrap">
          <div>
            <Title order={2}>Document Q&amp;A</Title>
            <Text size="sm" c="dimmed">Ask questions about your uploaded documents.</Text>
          </div>
          <Button
            variant="default"
            leftSection={<IconArrowLeft size={16} />}
            onClick={() => navigate('/app/ai/documents')}
          >
            Back to Documents
          </Button>
        </Group>

        {/* Document filter in Paper */}
        <Paper shadow="sm" p="md" radius="md" withBorder>
          <MultiSelect
            label="Filter by documents (optional)"
            placeholder="All documents"
            data={docOptions}
            value={selectedDocIds}
            onChange={setSelectedDocIds}
            searchable
            clearable
          />
        </Paper>

        {/* Chat messages area in Card */}
        <Card shadow="sm" padding={0} radius="md" style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <Group justify="space-between" p="lg" pb="sm">
            <Group gap="xs">
              <IconMessageChatbot size={20} />
              <Title order={4}>Conversation</Title>
            </Group>
            {messages.length > 0 && (
              <Badge variant="light" color="blue">{messages.length} messages</Badge>
            )}
          </Group>
          <Divider />

          <ScrollArea flex={1} viewportRef={viewport} offsetScrollbars p="md">
            <Stack gap="md" pb="md" p="md">
              {messages.length === 0 && (
                <Text c="dimmed" ta="center" py="xl">
                  Ask a question to get started. The AI will search your documents for answers.
                </Text>
              )}

              {messages.map((msg, idx) => (
                <Card
                  key={idx}
                  shadow="xs"
                  padding="md"
                  radius="md"
                  withBorder
                  style={{
                    alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start',
                    maxWidth: '85%',
                    backgroundColor: msg.role === 'user'
                      ? (isDark ? 'var(--mantine-color-blue-9)' : 'var(--mantine-color-blue-0)')
                      : (isDark ? 'var(--mantine-color-dark-6)' : 'var(--mantine-color-gray-0)'),
                  }}
                >
                  <Group gap="xs" mb="xs">
                    {msg.role === 'user' ? <IconUser size={16} /> : <IconRobot size={16} />}
                    <Text fw={600} size="sm">
                      {msg.role === 'user' ? 'You' : 'AI Assistant'}
                    </Text>
                    {msg.confidence !== undefined && (
                      <Badge variant="light" size="xs" color="blue">
                        {Math.round(msg.confidence * 100)}% confidence
                      </Badge>
                    )}
                    {msg.durationMs !== undefined && (
                      <Badge variant="light" size="xs" color="gray">
                        {msg.durationMs}ms
                      </Badge>
                    )}
                  </Group>

                  {msg.role === 'assistant' ? (
                    <div
                      className="ai-markdown-content"
                      style={{ fontSize: '14px', lineHeight: 1.6 }}
                      dangerouslySetInnerHTML={{ __html: renderMarkdown(msg.content) }}
                    />
                  ) : (
                    <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{msg.content}</Text>
                  )}

                  {msg.role === 'assistant' && (
                    <Group justify="flex-end" mt="xs">
                      <Tooltip label={copiedIdx === idx ? 'Copied!' : 'Copy answer'}>
                        <ActionIcon
                          variant="subtle"
                          size="sm"
                          color={copiedIdx === idx ? 'green' : 'gray'}
                          onClick={() => handleCopy(msg.content, idx)}
                        >
                          {copiedIdx === idx ? <IconCheck size={16} /> : <IconCopy size={16} />}
                        </ActionIcon>
                      </Tooltip>
                    </Group>
                  )}

                  {msg.sources && msg.sources.length > 0 && (
                    <Accordion accordionVariant="contained" mt="sm">
                      <Accordion.Item value="sources">
                        <Accordion.Control>
                          <Text size="xs" fw={500}>Sources ({msg.sources.length})</Text>
                        </Accordion.Control>
                        <Accordion.Panel>
                          <Stack gap="xs">
                            {msg.sources.map((src, sIdx) => (
                              <Card key={sIdx} padding="xs" withBorder radius="sm"
                                style={{ backgroundColor: isDark ? 'var(--mantine-color-dark-7)' : 'var(--mantine-color-gray-0)' }}
                              >
                                <Group justify="space-between" mb={4}>
                                  <Text size="xs" fw={600}>{src.fileName}</Text>
                                  <Badge size="xs" variant="outline" color="blue">
                                    {Math.round(src.relevanceScore * 100)}% relevant
                                  </Badge>
                                </Group>
                                <Text size="xs" c="dimmed" lineClamp={3}>
                                  {src.chunkText}
                                </Text>
                              </Card>
                            ))}
                          </Stack>
                        </Accordion.Panel>
                      </Accordion.Item>
                    </Accordion>
                  )}
                </Card>
              ))}

              {isAsking && (
                <Group gap="xs" py="sm">
                  <Loader size="xs" />
                  <Text size="sm" c="dimmed">Searching documents...</Text>
                </Group>
              )}
            </Stack>
          </ScrollArea>
        </Card>

        {/* Input area in Paper */}
        <Paper shadow="sm" p="md" radius="md" withBorder>
          <Group gap="sm">
            <TextInput
              flex={1}
              placeholder="Ask a question about your documents..."
              value={question}
              onChange={(e) => setQuestion(e.currentTarget.value)}
              onKeyDown={handleKeyDown}
              disabled={isAsking}
            />
            <Button
              leftSection={<IconSend size={16} />}
              onClick={handleAsk}
              loading={isAsking}
              disabled={!question.trim()}
            >
              Send
            </Button>
          </Group>
        </Paper>
      </Stack>
    </Container>
  );
}
